import React from 'react';

import HelpContent from './help-content';
import { mui } from '../libs';
import * as types from '../types';

type CardContainerToggle = {
  checked: boolean;
  onChange: (_checked: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
};

type CardContainerProps = React.ComponentProps<typeof mui.core.Box> & {
  children: React.ReactNode;
  helpContent?: types.helpItem.HelpItemContent;
  title?: string | Element;
  titleSize?: 'small' | 'large';
  onClick?: () => void;
  mb?: number;
  toggle?: CardContainerToggle;
  labelError?: boolean;
  errorState?: boolean;
  showDivider?: boolean;
  count?: number;
};

const DefCardContainer = ({
  children,
  helpContent,
  title,
  titleSize = 'small',
  onClick,
  mb = 7,
  toggle,
  labelError,
  errorState,
  showDivider = false,
  count,
  ...boxProps
}: CardContainerProps) => {
  const isMobile = mui.core.useMediaQuery('(max-width:960px)');

  const TitleText = title ? (
    <mui.core.Typography
      variant={titleSize === 'large' ? 'h4' : 'body2'}
      color={labelError || errorState ? 'error' : 'text.primary'}
      sx={{
        fontWeight: 500,
        textAlign: isMobile ? 'left' : 'right',
        mt: 0.5,
      }}
    >
      {title}
    </mui.core.Typography>
  ) : null;

  return (
    <mui.core.Box mb={mb} {...boxProps} onClick={onClick}>
      <mui.core.Grid container spacing={4} alignItems="flex-start">
        <mui.core.Grid size={{ xs: 12, md: 2, lg: 3 }} mt={2} mb={isMobile ? 4 : 0}>
          <mui.core.Stack
            gap={2}
            flexDirection="row"
            alignItems="center"
            justifyContent={isMobile ? 'flex-start' : 'flex-end'}
          >
            <mui.core.Box display="flex" alignItems="center" gap={1}>
              {TitleText}
              {count !== undefined && (
                <mui.core.Chip
                  label={count}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, '& .MuiChip-label': { px: '6px' } }}
                />
              )}
            </mui.core.Box>
            {helpContent ? <HelpContent helpContent={helpContent} /> : null}
          </mui.core.Stack>
        </mui.core.Grid>

        <mui.core.Grid size={{ xs: 12, md: 10, lg: 9 }} sx={{ position: 'relative' }}>
          {toggle && (
            <mui.core.Box display="flex" alignItems="center" gap={2} my={2}>
              <mui.core.Switch
                checked={toggle.checked}
                onChange={(_e, checked) => toggle.onChange(checked)}
                disabled={toggle.disabled}
                color="primary"
                inputProps={{ 'aria-label': toggle.ariaLabel || 'toggle' }}
              />
            </mui.core.Box>
          )}

          {toggle ? (
            <mui.core.Collapse in={toggle.checked} unmountOnExit>
              <mui.core.Box mt={2}>{children}</mui.core.Box>
            </mui.core.Collapse>
          ) : (
            <mui.core.Box>{children}</mui.core.Box>
          )}
        </mui.core.Grid>
      </mui.core.Grid>

      {showDivider && <mui.core.Divider sx={{ mt: 3, mb: 0 }} />}
    </mui.core.Box>
  );
};

export default DefCardContainer;
